import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, skipToken } from '@apollo/client/react';
import { useLocation, useNavigate, useParams } from 'react-router';

import { PageDrawer } from 'common/components/PageDrawer';
import { IPageDrawerProps } from 'common/components/PageDrawer/PageDrawer';
import { PageTitle } from 'common/components/PageTitle';
import { GroupForm } from 'user/components/GroupForm';
import {
    IContentTypeValue,
    IFeature,
    DeleteGroupDocument,
    GroupDocument,
    UpdateGroupDocument,
} from 'graphql/types';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { getExtraCategoryInitialFormValues } from 'organisation/utils/extraCategory';
import { Loader } from 'common/components/Loader';
import { useApolloError } from 'common/hooks/useApolloError';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { useExtraCategoryValues } from 'common/hooks/useExtraCategoryValues';
import { closeGlobalDrawerRoute } from 'common/utils/globalDrawer';
import { EGroupFormTab, TGroupUpdateFormValues } from 'user/types';
import { useFeature } from 'common/hooks/useFeature';

export interface IProps extends IPageDrawerProps {
    id?: string;
    initialTab?: EGroupFormTab;
    onClose?: () => void;
    onDelete?(): void;
    onUpdate?(
        initialValues: TGroupUpdateFormValues,
        newValues: TGroupUpdateFormValues
    ): void;
    onUserUpdate?(): void;
}

export const GroupUpdateDrawer = ({
    id: groupId,
    initialTab,
    open,
    onClose,
    onDelete,
    onUpdate,
    onUserUpdate,
}: IProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const { id: groupIdParam } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { canUse: canUseSkills } = useFeature(IFeature.Skill);

    const id = groupId || groupIdParam;

    const {
        data: groupData,
        loading: groupLoading,
        error: groupError,
    } = useQuery(
        GroupDocument,
        id ? { variables: { id, withSkills: canUseSkills } } : skipToken
    );

    useEffect(() => {
        if (groupError) {
            showApolloError(groupError);
        }
    }, [groupError, showApolloError]);
    const [updateGroup, { loading: updateGroupLoading }] =
        useMutation(UpdateGroupDocument);
    const [deleteGroup, { loading: deleteGroupLoading }] =
        useMutation(DeleteGroupDocument);

    const {
        extraCategories,
        loading: extraCategoriesLoading,
        handleUpdateExtraCategoryValues,
    } = useExtraCategoryValues({
        contentType: IContentTypeValue.Group,
        modelId: id || '',
        skipQuery: !id,
        typeNames: ['Group'],
    });

    const { group } = groupData || {};

    const {
        name,
        enableChat,
        maxUsers,
        ref,
        trainings,
        extraCategoryValues,
        isEditable,
        skillRequirements,
    } = group || {};

    const initialExtraCategoryValues = getExtraCategoryInitialFormValues(
        extraCategories,
        extraCategoryValues
    );

    const initialValues: TGroupUpdateFormValues = {
        name: name || '',
        enableChat: !!enableChat,
        ref: ref || '',
        maxUsers: maxUsers || 0,
        trainingIds: trainings,
        extraCategories: {
            ...initialExtraCategoryValues,
        },
        skills: skillRequirements || [],
    };

    const handleSubmit = async (values: TGroupUpdateFormValues) => {
        const {
            maxUsers,
            extraCategories: extraCategoryUpdateValues,
            trainingIds: trainings,
            skills,
            participantIds: _participantIds,
            managerIds: _managerIds,
            ...otherValues
        } = values;

        const skillsInput = skills
            ?.filter((skillReq) => skillReq.skill?.id)
            .map((skillReq) => ({
                skillId: skillReq.skill!.id,
                minAbsolute: skillReq.minAbsolute
                    ? parseInt(String(skillReq.minAbsolute), 10)
                    : null,
                minPercent: skillReq.minPercent
                    ? parseInt(String(skillReq.minPercent), 10)
                    : null,
            }));

        try {
            await updateGroup({
                variables: {
                    id,
                    group: {
                        trainingIds: trainings?.map((training) => training.id),
                        maxUsers: maxUsers ? +maxUsers : 0,
                        skills: skillsInput?.length ? skillsInput : undefined,
                        ...otherValues,
                    },
                    withSkills: canUseSkills,
                },
                update: (cache) => {
                    const initialTrainingIds = (initialValues.trainingIds || [])
                        .map((training) => training?.id)
                        .filter(Boolean)
                        .sort();
                    const submittedTrainingIds = (trainings || [])
                        .map((training) => training?.id)
                        .filter(Boolean)
                        .sort();
                    const trainingsChanged =
                        JSON.stringify(initialTrainingIds) !==
                        JSON.stringify(submittedTrainingIds);
                    const nameChanged = values.name !== group?.name;

                    if (nameChanged || trainingsChanged) {
                        cache.evict({ fieldName: 'paginatedGroups' });
                    }
                },
            });

            await handleUpdateExtraCategoryValues(
                extraCategoryValues || [],
                extraCategoryUpdateValues
            );
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(
            translate('groupsManagementPage.groupsActionSuccess.update'),
            {
                variant: 'success',
            }
        );

        handleClose();
        onUpdate?.(initialValues, values);

        return;
    };

    const handleDelete = async () => {
        if (!id) return;

        try {
            await deleteGroup({
                variables: { id },
                update: (cache) => {
                    // Remove deleted group from cache to make sure they are gone throughout the system
                    cache.evict({ id: `Group:${id}` });

                    cache.evict({ fieldName: 'paginatedGroups' });
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(
            translate('groupsManagementPage.groupsActionSuccess.delete'),
            {
                variant: 'success',
            }
        );

        onDelete?.();

        handleClose();
    };

    const handleClose = () => {
        setShowDeleteDialog(false);

        if (!!groupIdParam) {
            closeGlobalDrawerRoute(navigate, location);

            return;
        }

        onClose?.();
    };

    const loading = groupLoading || extraCategoriesLoading;

    return (
        <PageDrawer open={open} onClose={handleClose}>
            <PageTitle>
                {`${translate('groupsManagementPage.pageTitle')} - ${translate(
                    'groupForm.updateGroup'
                )}`}
            </PageTitle>
            <Box sx={{ p: { xs: 2, sm: 4 }, pt: { xs: 0, sm: 0 } }}>
                {loading && <Loader />}

                {!loading && group && (
                    <GroupForm
                        isEdit
                        disabled={updateGroupLoading || extraCategoriesLoading}
                        group={group}
                        initialTab={initialTab}
                        initialValues={initialValues}
                        isEditable={isEditable}
                        onClose={handleClose}
                        onDelete={() => setShowDeleteDialog(true)}
                        onSubmit={handleSubmit}
                        onUserUpdate={onUserUpdate}
                    />
                )}
            </Box>
            <ConfirmDialog
                confirmText={translate('delete')}
                loading={deleteGroupLoading}
                open={showDeleteDialog}
                title={translate(
                    'groupsManagementPage.groupDeleteDialog.title'
                )}
                onCancel={() => {
                    setShowDeleteDialog(false);
                }}
                onClose={() => setShowDeleteDialog(false)}
                onConfirm={handleDelete}
            >
                {translate('groupsManagementPage.groupDeleteDialog.text')}
            </ConfirmDialog>
        </PageDrawer>
    );
};
